/**
 * EdgeDeviceTable
 *
 * The edge-device list, shared by the super-admin global view
 * (/admin/edge-devices) and the "Your edges" section of the tenant Edges page.
 * Extracted from pages/admin/EdgeDevices.tsx so both surfaces render identical
 * columns, filters and tier gating.
 *
 * Tier visibility (production / demo / lab) is applied here, BEFORE the
 * user-controllable filters, so a tenant without lab access can never see lab
 * devices regardless of how they fiddle the deviceTier dropdown.
 */

import React, { useMemo } from 'react';
import type { CentralEdgeDevice } from '@simcheck/shared-types';
import { Column, Card } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { StatusBadge } from '@/components/admin/edge/StatusBadge';
import {
  useDataTableFilters,
  DataTableFilter,
  createColumnConfigHelper,
  applyFilters,
} from '@/components/data-table-filter';
import { MobileFilterSheet } from '@/components/layout/MobileFilterSheet';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useDeviceVisibility, filterDevicesByTier } from '@/hooks/useDeviceVisibility';
import {
  Server,
  CircleDot,
  MapPin,
  Hash,
  Wifi,
  Clock,
  FlaskConical,
  Cpu,
  HardDrive,
  Thermometer,
  Network,
} from 'lucide-react';

/**
 * The device's identifying wired MAC: eth1 when present (the USB Ethernet
 * adapter on our standard build), otherwise eth0 — some Pis have no eth1 at
 * all and only report eth0 + wlan0.
 */
function primaryMac(row: CentralEdgeDevice): { iface: 'eth1' | 'eth0'; mac: string } | null {
  const nics = row.networkInterfaces;
  if (nics?.eth1) return { iface: 'eth1', mac: nics.eth1 };
  if (nics?.eth0) return { iface: 'eth0', mac: nics.eth0 };
  return null;
}

const filterColumnHelper = createColumnConfigHelper<CentralEdgeDevice>();

const getStaticFilterConfigs = () => [
  filterColumnHelper
    .text()
    .id('name')
    .accessor((row) => row.name)
    .displayName('Name')
    .icon(Server)
    .build(),
  filterColumnHelper
    .text()
    .id('deviceId')
    .accessor((row) => row.deviceId || row.device_id)
    .displayName('Device ID')
    .icon(Hash)
    .build(),
  filterColumnHelper
    .option()
    .id('status')
    .accessor((row) => row.status)
    .displayName('Status')
    .icon(CircleDot)
    .options([
      { label: 'Online', value: 'online' },
      { label: 'Offline', value: 'offline' },
      { label: 'Error', value: 'error' },
      { label: 'Connecting', value: 'connecting' },
    ])
    .build(),
  filterColumnHelper
    .text()
    .id('ipAddress')
    .accessor((row) => row.ipAddress || '')
    .displayName('IP Address')
    .icon(Wifi)
    .build(),
  filterColumnHelper
    .text()
    .id('eth1Mac')
    .accessor((row) => primaryMac(row)?.mac || '')
    .displayName('MAC')
    .icon(Network)
    .build(),
  filterColumnHelper
    .option()
    .id('deviceTier')
    .accessor((row) => row.deviceTier || (row.isDemo ? 'demo' : 'production'))
    .displayName('Device Tier')
    .icon(FlaskConical)
    .options([
      { label: 'Production', value: 'production' },
      { label: 'Demo', value: 'demo' },
      { label: 'Lab', value: 'lab' },
    ])
    .build(),
];

export interface EdgeDeviceTableProps {
  devices: CentralEdgeDevice[];
  loading: boolean;
  /** Renders the Owner column + Owner filter chip (cross-org views only). */
  showOwnerColumn?: boolean;
  /** orgId -> display name, for the Owner column. */
  orgNames?: Map<string, string>;
  activeOrganizationId?: string | null;
  onRowClick: (device: CentralEdgeDevice) => void;
  emptyMessage?: string;
  searchPlaceholder?: string;
  /** Hide the filter bar entirely (small owned fleets don't need it). */
  showFilters?: boolean;
  defaultPageSize?: number;
  /** Called with the tier-visible device set so callers can render stats. */
  onVisibleDevicesChange?: (devices: CentralEdgeDevice[]) => void;
}

export const EdgeDeviceTable: React.FC<EdgeDeviceTableProps> = ({
  devices,
  loading,
  showOwnerColumn = false,
  orgNames,
  activeOrganizationId,
  onRowClick,
  emptyMessage,
  searchPlaceholder = 'Search by name, device ID, location, or IP address...',
  showFilters = true,
  defaultPageSize = 10,
}) => {
  const fmt = useDateFormat();
  const visibilityFlags = useDeviceVisibility();

  const ownerLabel = (ownerOrgId: string | null | undefined): string => {
    if (!ownerOrgId) return 'Shared';
    return orgNames?.get(ownerOrgId) || ownerOrgId;
  };

  const filterColumnsConfig = useMemo(() => {
    const uniqueLocations = Array.from(
      new Set(devices.map((d) => d.locationName).filter(Boolean)),
    ) as string[];

    return [
      ...getStaticFilterConfigs(),
      filterColumnHelper
        .option()
        .id('locationName')
        .accessor((row) => row.locationName || '')
        .displayName('Location')
        .icon(MapPin)
        .options(uniqueLocations.map((loc) => ({ label: loc, value: loc })))
        .build(),
      filterColumnHelper
        .option()
        .id('lastHeartbeat')
        .accessor((row) => {
          if (!row.lastHeartbeat) return 'never';
          const now = new Date();
          const heartbeat =
            row.lastHeartbeat instanceof Date ? row.lastHeartbeat : new Date(row.lastHeartbeat);
          const diffMins = (now.getTime() - heartbeat.getTime()) / (1000 * 60);
          if (diffMins <= 5) return '5m';
          if (diffMins <= 60) return '1h';
          if (diffMins <= 1440) return '24h';
          if (diffMins <= 10080) return '7d';
          return 'older';
        })
        .displayName('Last Seen')
        .icon(Clock)
        .options([
          { label: 'Last 5 minutes', value: '5m' },
          { label: 'Last hour', value: '1h' },
          { label: 'Last 24 hours', value: '24h' },
          { label: 'Last 7 days', value: '7d' },
          { label: 'Older', value: 'older' },
          { label: 'Never', value: 'never' },
        ])
        .build(),
      // Owner filter — only useful when the view may show devices from
      // multiple orgs. In a single-org view the chip would have one value.
      ...(showOwnerColumn
        ? [
            filterColumnHelper
              .option()
              .id('owner')
              .accessor((row) => ((row as any).ownerOrgId ?? '__shared__') as string)
              .displayName('Owner')
              .icon(Server)
              .options([
                { label: 'Shared', value: '__shared__' },
                ...Array.from(orgNames?.entries() ?? [])
                  .map(([id, name]) => ({ label: name, value: id }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              ])
              .build(),
          ]
        : []),
    ];
  }, [devices, showOwnerColumn, orgNames]);

  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: devices,
    columnsConfig: filterColumnsConfig,
    defaultFilters: [
      { columnId: 'deviceTier', type: 'option', operator: 'is', values: ['production'] },
    ],
  });

  const tierVisibleDevices = useMemo(
    () => filterDevicesByTier(devices, visibilityFlags),
    [devices, visibilityFlags],
  );

  const filteredDevices = useMemo(
    () => applyFilters(tierVisibleDevices, filters, filterColumnsConfig),
    [tierVisibleDevices, filters, filterColumnsConfig],
  );

  const formatLastSeen = (date?: Date) => {
    if (!date) return 'Never';
    const diffMins = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const columns: Column<CentralEdgeDevice>[] = [
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: '10%',
      render: (value: unknown) => (
        <StatusBadge status={value as 'online' | 'offline' | 'error' | 'connecting'} size="sm" />
      ),
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      width: '15%',
      render: (value: unknown, row: CentralEdgeDevice) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{value as string}</span>
          {(row.deviceTier === 'demo' || row.isDemo) && (
            <Badge
              variant="outline"
              className="text-xs bg-purple-500/10 text-purple-500 border-purple-500/30"
            >
              Demo
            </Badge>
          )}
          {row.deviceTier === 'lab' && (
            <Badge
              variant="outline"
              className="text-xs bg-amber-500/10 text-amber-500 border-amber-500/30"
            >
              Lab
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'deviceId',
      header: 'Device ID',
      sortable: true,
      width: '20%',
      render: (value: unknown, row: CentralEdgeDevice) => (
        <code className="text-sm text-muted-foreground">{(value as string) || row.device_id}</code>
      ),
    },
    ...(showOwnerColumn
      ? [
          {
            key: 'ownerOrgId',
            header: 'Owner',
            sortable: true,
            width: '12%',
            render: (_value: unknown, row: CentralEdgeDevice) => {
              const owner = (row as any).ownerOrgId ?? null;
              if (!owner) {
                return (
                  <Badge
                    variant="outline"
                    className="text-xs bg-muted/40 text-muted-foreground border-border"
                  >
                    Shared
                  </Badge>
                );
              }
              const label = ownerLabel(owner);
              const isMine = owner === activeOrganizationId;
              return (
                <Badge
                  variant="outline"
                  className={
                    isMine
                      ? 'text-xs bg-accent-blue/10 text-accent-light-blue border-accent-blue/30'
                      : 'text-xs'
                  }
                >
                  {label}
                  {isMine ? ' (yours)' : ''}
                </Badge>
              );
            },
          } as Column<CentralEdgeDevice>,
        ]
      : []),
    {
      // Key kept as `eth1Mac` so saved filters/column settings still apply.
      key: 'eth1Mac',
      header: 'MAC',
      sortable: true,
      width: '12%',
      sortValue: (row: CentralEdgeDevice) => primaryMac(row)?.mac || '',
      render: (_value: unknown, row: CentralEdgeDevice) => {
        const nic = primaryMac(row);
        if (!nic) return <span className="text-sm text-muted-foreground">—</span>;
        return (
          <span className="text-sm text-muted-foreground font-mono" title={`MAC of ${nic.iface}`}>
            {nic.mac}
            {nic.iface === 'eth0' && <span className="ml-1 text-xs text-muted-foreground/60">(eth0)</span>}
          </span>
        );
      },
    },
    {
      key: 'ipAddress',
      header: 'IP Address',
      sortable: true,
      width: '15%',
      render: (value: unknown, row: CentralEdgeDevice) => (
        <div>
          <div className="text-sm text-foreground font-mono">{(value as string) || 'N/A'}</div>
          {row.externalIpAddress && (
            <div className="text-xs text-muted-foreground/70 font-mono">
              Ext: {row.externalIpAddress}
            </div>
          )}
          {row.backhaulStatus?.active && row.backhaulStatus.isDefaultRoute && (
            <div
              className="text-xs text-accent-blue"
              title="Host traffic is riding the backhaul modem (no WiFi/Ethernet uplink)"
            >
              via cellular backhaul
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      width: '20%',
      render: (value: unknown, row: CentralEdgeDevice) => (
        <div className="text-sm">
          {row.locationName && (
            <div className="font-medium text-foreground mb-1">📍 {row.locationName}</div>
          )}
          {row.address && (
            <div className="text-muted-foreground text-xs max-w-xs truncate">{row.address}</div>
          )}
          {row.latitude && row.longitude && (
            <div className="text-muted-foreground/70 text-xs font-mono mt-0.5">
              {row.latitude.toFixed(6)}, {row.longitude.toFixed(6)}
            </div>
          )}
          {!row.locationName && !row.address && (
            <div className="text-muted-foreground/70 text-xs">{(value as string) || 'N/A'}</div>
          )}
        </div>
      ),
    },
    {
      key: 'lastHeartbeat',
      header: 'Last Heartbeat',
      sortable: true,
      width: '15%',
      render: (value: unknown, row: CentralEdgeDevice) => {
        // Demo devices don't have real heartbeats.
        if (row.isDemo || row.deviceTier === 'demo') {
          return (
            <div className="flex items-center gap-1.5">
              <FlaskConical className="w-3.5 h-3.5 text-purple-500" />
              <span className="text-sm text-purple-500">Simulated</span>
            </div>
          );
        }
        const dateValue = value as Date | undefined;
        return (
          <div>
            <div className="text-sm text-foreground">{formatLastSeen(dateValue)}</div>
            {dateValue && (
              <div className="text-xs text-muted-foreground/70">
                {fmt.formatDateTime(dateValue)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'softwareVersion',
      header: 'Version',
      sortable: true,
      width: '8%',
      render: (value: unknown, row: CentralEdgeDevice) => {
        const version = (value as string) || (row as any).version;
        if (!version) return <span className="text-xs text-muted-foreground/50">—</span>;
        const hasUpdate = row.updateStatus === 'update_available';
        const isUpdating = row.updateStatus === 'updating';
        const updateFailed = row.updateStatus === 'update_failed';
        return (
          <div className="flex items-center gap-1.5">
            <code className="text-xs text-muted-foreground">{version}</code>
            {hasUpdate && (
              <span className="w-2 h-2 rounded-full bg-yellow-500" title="Update available" />
            )}
            {isUpdating && (
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" title="Updating" />
            )}
            {updateFailed && (
              <span className="w-2 h-2 rounded-full bg-destructive" title="Update failed" />
            )}
          </div>
        );
      },
    },
    {
      key: 'health',
      header: 'Health',
      sortable: false,
      width: '12%',
      render: (_value: unknown, row: CentralEdgeDevice) => {
        const h = row.health;
        if (!h || h.cpuPercent == null)
          return <span className="text-xs text-muted-foreground/50">—</span>;
        const cpuColor =
          h.cpuPercent! > 80
            ? 'text-destructive'
            : h.cpuPercent! > 60
              ? 'text-yellow-500'
              : 'text-emerald-500';
        const memColor =
          h.memoryPercent! > 80
            ? 'text-destructive'
            : h.memoryPercent! > 60
              ? 'text-yellow-500'
              : 'text-emerald-500';
        return (
          <div className="flex items-center gap-2 text-xs">
            <span className={`flex items-center gap-0.5 ${cpuColor}`} title={`CPU ${h.cpuPercent}%`}>
              <Cpu className="w-3 h-3" />
              {h.cpuPercent}%
            </span>
            <span
              className={`flex items-center gap-0.5 ${memColor}`}
              title={`Memory ${h.memoryPercent}%`}
            >
              <HardDrive className="w-3 h-3" />
              {h.memoryPercent}%
            </span>
            {h.temperature != null && (
              <span
                className={`flex items-center gap-0.5 ${h.temperature > 70 ? 'text-destructive' : 'text-muted-foreground'}`}
                title={`Temperature ${h.temperature}°C`}
              >
                <Thermometer className="w-3 h-3" />
                {h.temperature}°
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'modems',
      header: 'Modems',
      sortable: true,
      width: '5%',
      render: (value: unknown) => (
        <div className="text-sm font-semibold text-foreground">
          {Array.isArray(value) ? value.length : 0}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {showFilters && (
        <MobileFilterSheet activeCount={filters.length}>
          <Card className="p-4">
            <DataTableFilter
              columns={filterColumns}
              filters={filters}
              actions={actions}
              strategy={strategy}
            />
          </Card>
        </MobileFilterSheet>
      )}

      <ResponsiveTable
        columns={columns}
        data={filteredDevices}
        keyExtractor={(row) => row.id}
        searchable
        searchPlaceholder={searchPlaceholder}
        emptyMessage={
          devices.length === 0
            ? (emptyMessage ??
              'No edge devices found. Devices will appear here once they register with the central server.')
            : 'No devices match your filters'
        }
        loading={loading}
        onRowClick={onRowClick}
        paginated
        defaultPageSize={defaultPageSize}
      />
    </div>
  );
};

/** Tier-visible device set — for stat tiles that must agree with the table. */
export function useTierVisibleDevices(devices: CentralEdgeDevice[]): CentralEdgeDevice[] {
  const visibilityFlags = useDeviceVisibility();
  return useMemo(() => filterDevicesByTier(devices, visibilityFlags), [devices, visibilityFlags]);
}
